import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Store,
  ShoppingBag,
  Package,
  ArrowRightLeft,
  TrendingUp,
  Plus,
  RefreshCw,
  PackagePlus,
  Search,
  Edit2,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Warehouse,
  ArrowRight,
  Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { saveToLocalDB, getFromLocalDB, addToSyncQueue } from '@/lib/offlineStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const NASShop = () => {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // All products from master list
  const [sales, setSales] = useState([]);
  const [stockHistory, setStockHistory] = useState([]); // Stock movement history
  const [activeTab, setActiveTab] = useState('wholesale');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingProduct, setEditingProduct] = useState(null); // Product being edited
  const [editQuantity, setEditQuantity] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const [saleForm, setSaleForm] = useState({
    productId: '',
    quantity: 0,
    saleType: 'wholesale',
    customerName: '',
    paymentMethod: 'cash',
    discount: 0
  });
  
  const [convertForm, setConvertForm] = useState({
    productId: '',
    sacksToConvert: 1,
    sourceUnit: 'sack',  // What we're converting from
    targetUnit: 'kilogram',  // What we're converting to
    conversionRate: 50  // How many target units per source unit (e.g., 50 kg per sack)
  });
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  
  const [isTransferToBodegaOpen, setIsTransferToBodegaOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    productId: '',
    quantity: 0,
    stockType: 'wholesale',
    notes: ''
  });
  
  const [addStockForm, setAddStockForm] = useState({
    productId: '',
    quantity: 0,
    stockType: 'sack' // Default to sack
  });
  
  // Helper to get unit label from stock type for Add Stock form
  const getStockUnitLabel = (stockType) => {
    const labels = {
      'wholesale': 'units',
      'sack': 'sacks',
      'box': 'boxes',
      'case': 'cases',
      'retail': 'units',
      'kilogram': 'kg',
      'pack': 'packs',
      'piece': 'pieces',
      'bottle': 'bottles'
    };
    return labels[stockType] || stockType;
  };
  
  // Helper to determine if stock type is wholesale category
  const isWholesaleType = (stockType) => {
    return ['wholesale', 'sack', 'box', 'case'].includes(stockType);
  };
  
  // Helper to get wholesale unit from product's unitType
  const getWholesaleUnit = (product) => {
    const unitType = product?.unitType || '';
    if (unitType.includes('SACK') || unitType.includes('KG')) return { singular: 'sack', plural: 'sacks' };
    if (unitType.includes('BOX') || unitType.includes('PIECE')) return { singular: 'box', plural: 'boxes' };
    if (unitType.includes('CASE') || unitType.includes('BOTTLE')) return { singular: 'case', plural: 'cases' };
    // Default fallback
    return { singular: 'sack', plural: 'sacks' };
  };
  
  // Helper to get retail unit from product's unitType
  const getRetailUnit = (product) => {
    const unitType = product?.unitType || '';
    if (unitType.includes('KG') || unitType.includes('SACK')) return { singular: 'kg', plural: 'kg' };
    if (unitType.includes('PACK') || unitType.includes('BOX')) return { singular: 'pack', plural: 'packs' };
    if (unitType.includes('PIECE')) return { singular: 'piece', plural: 'pieces' };
    if (unitType.includes('BOTTLE') || unitType.includes('CASE')) return { singular: 'bottle', plural: 'bottles' };
    // Default fallback
    return { singular: 'kg', plural: 'kg' };
  };

  // Filter products based on search query
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    try {
      if (navigator.onLine) {
        // Load all products from master list (shows all products with their shop stock)
        const allProductsResponse = await axios.get(`${API}/api/nas/products`);
        setAllProducts(allProductsResponse.data);
        setProducts(allProductsResponse.data); // Use all products for display
        await saveToLocalDB('nas_all_products', allProductsResponse.data);
        
        // Load sales history
        const salesResponse = await axios.get(`${API}/api/nas/shop/sales`);
        setSales(salesResponse.data);
        await saveToLocalDB('nas_shop_sales', salesResponse.data);
        
        // Load stock movement history
        const historyResponse = await axios.get(`${API}/api/nas/shop/stock-history?limit=200`);
        setStockHistory(historyResponse.data);
        await saveToLocalDB('nas_stock_history', historyResponse.data);
      } else {
        const localAllProducts = await getFromLocalDB('nas_all_products');
        const localSales = await getFromLocalDB('nas_shop_sales');
        const localHistory = await getFromLocalDB('nas_stock_history');
        if (localAllProducts) {
          setAllProducts(localAllProducts);
          setProducts(localAllProducts);
        }
        if (localSales) setSales(localSales);
        if (localHistory) setStockHistory(localHistory);
        toast.info('Offline mode - showing cached data');
      }
    } catch (error) {
      console.error('Error loading shop data:', error);
      const localAllProducts = await getFromLocalDB('nas_all_products');
      const localSales = await getFromLocalDB('nas_shop_sales');
      const localHistory = await getFromLocalDB('nas_stock_history');
      if (localAllProducts) {
        setAllProducts(localAllProducts);
        setProducts(localAllProducts);
      }
      if (localSales) setSales(localSales);
      if (localHistory) setStockHistory(localHistory);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRecordSale = async () => {
    if (!saleForm.productId || saleForm.quantity <= 0) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        const response = await axios.post(`${API}/api/nas/shop/sale`, saleForm);
        toast.success(`Sale recorded: ₱${response.data.total?.toLocaleString() || 0}`);
        loadData(); // Refresh data
      } else {
        await addToSyncQueue({ 
          type: 'RECORD_SALE', 
          data: saleForm, 
          timestamp: new Date().toISOString() 
        });
        toast.success('Sale recorded (will sync when online)');
      }
      
      setSaleForm({ productId: '', quantity: 0, saleType: activeTab, customerName: '', paymentMethod: 'cash', discount: 0 });
      setIsSaleModalOpen(false);
    } catch (error) {
      console.error('Error recording sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to record sale');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleConvertStock = async () => {
    if (!convertForm.productId || convertForm.sacksToConvert <= 0) {
      toast.error('Please select product and quantity to convert');
      return;
    }
    
    const product = allProducts.find(p => p.id === convertForm.productId);
    if (!product) {
      toast.error('Product not found');
      return;
    }
    
    const wholesaleStock = product.shopStock?.wholesale || 0;
    if (convertForm.sacksToConvert > wholesaleStock) {
      toast.error(`Insufficient wholesale stock. Only ${wholesaleStock} available.`);
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (navigator.onLine) {
        const params = new URLSearchParams({
          product_id: convertForm.productId,
          sacks_to_convert: convertForm.sacksToConvert,
          conversion_rate: convertForm.conversionRate || product.conversionRate || 50
        });
        const response = await axios.post(`${API}/api/nas/shop/convert?${params}`);
        toast.success(response.data.message);
        loadData(); // Refresh data
      } else {
        await addToSyncQueue({ 
          type: 'CONVERT_STOCK', 
          data: convertForm, 
          timestamp: new Date().toISOString() 
        });
        toast.success('Conversion queued (will sync when online)');
      }
      
      setConvertForm({ productId: '', sacksToConvert: 1, sourceUnit: 'sack', targetUnit: 'kilogram', conversionRate: 50 });
      setIsConvertModalOpen(false);
    } catch (error) {
      console.error('Error converting stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert stock');
    } finally {
      setIsSaving(false);
    }
  };
  
  const openConvertModal = (product) => {
    // Determine source and target units from product's unitType
    const wholesaleUnit = getWholesaleUnit(product);
    const retailUnit = getRetailUnit(product);
    
    // Get conversion rate from product or use default
    const defaultRate = product.conversionRate || 50;
    
    setConvertForm({
      productId: product.id,
      sacksToConvert: 1,
      sourceUnit: wholesaleUnit.singular,
      targetUnit: retailUnit.singular,
      conversionRate: defaultRate
    });
    setIsConvertModalOpen(true);
  };
  
  const handleAddStock = async () => {
    if (!addStockForm.productId) {
      toast.error('Please select a product');
      return;
    }
    if (addStockForm.quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const product = allProducts.find(p => p.id === addStockForm.productId);
      if (!product) {
        toast.error('Product not found');
        return;
      }
      
      // Determine the stock category (wholesale or retail) based on unit type
      const stockCategory = isWholesaleType(addStockForm.stockType) ? 'wholesale' : 'retail';
      
      // Call dedicated API endpoint to add shop stock
      const response = await axios.post(`${API}/api/nas/products/${addStockForm.productId}/add-shop-stock`, {
        quantity: addStockForm.quantity,
        stockType: stockCategory,
        unit: addStockForm.stockType // Pass the specific unit type for logging
      });
      
      const unitLabel = getStockUnitLabel(addStockForm.stockType);
      toast.success(`Added ${addStockForm.quantity} ${unitLabel} of "${product.name}" to shop stock!`);
      
      // Reset form
      setAddStockForm({
        productId: '',
        quantity: 0,
        stockType: 'sack'
      });
      setIsAddStockModalOpen(false);
      loadData(); // Refresh products list
    } catch (error) {
      console.error('Error adding stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to add stock');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleEditStock = async (stockType) => {
    if (!editingProduct) return;
    
    setIsSaving(true);
    try {
      const currentStock = editingProduct.shopStock || { wholesale: 0, retail: 0 };
      const updatedShopStock = {
        ...currentStock,
        [stockType]: editQuantity
      };
      
      await axios.put(`${API}/api/nas/products/${editingProduct.id}`, {
        shopStock: updatedShopStock
      });
      
      const unitLabel = stockType === 'wholesale' ? 'sacks' : 'kg/packs';
      toast.success(`Updated ${editingProduct.name} stock to ${editQuantity} ${unitLabel}`);
      
      setEditingProduct(null);
      setEditQuantity(0);
      setIsEditModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    } finally {
      setIsSaving(false);
    }
  };
  
  const openEditModal = (product, stockType) => {
    setEditingProduct(product);
    const currentQty = stockType === 'wholesale' 
      ? (product.shopStock?.wholesale || 0)
      : (product.shopStock?.retail || 0);
    setEditQuantity(currentQty);
    setIsEditModalOpen(true);
  };
  
  const handleTransferToBodega = async () => {
    if (!transferForm.productId || transferForm.quantity <= 0) {
      toast.error('Please select a product and enter a valid quantity');
      return;
    }
    
    const product = allProducts.find(p => p.id === transferForm.productId);
    if (!product) {
      toast.error('Product not found');
      return;
    }
    
    const available = transferForm.stockType === 'wholesale'
      ? (product.shopStock?.wholesale || 0)
      : (product.shopStock?.retail || 0);
    
    if (transferForm.quantity > available) {
      toast.error(`Insufficient stock. Only ${available} available.`);
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/api/nas/shop/transfer-to-bodega`, transferForm);
      toast.success(response.data.message);
      setTransferForm({ productId: '', quantity: 0, stockType: 'wholesale', notes: '' });
      setIsTransferToBodegaOpen(false);
      loadData();
    } catch (error) {
      console.error('Error transferring to bodega:', error);
      toast.error(error.response?.data?.detail || 'Failed to transfer stock to Bodega');
    } finally {
      setIsSaving(false);
    }
  };
  
  const todaySales = sales.filter(s => s.timestamp?.startsWith(new Date().toISOString().split('T')[0]));
  const todayTotal = todaySales.reduce((sum, s) => sum + (s.total || 0), 0);
  const wholesaleSales = todaySales.filter(s => s.type === 'wholesale');
  const retailSales = todaySales.filter(s => s.type === 'retail');
  
  const getUnitLabel = (product, type) => {
    const unitType = product?.unitType || '';
    if (type === 'wholesale') {
      // Get wholesale unit (the "from" part of conversion)
      if (unitType.includes('CASE')) return 'case';
      if (unitType.includes('BOX')) return 'box';
      return 'sack';
    }
    // Get retail unit (the "to" part of conversion)
    if (unitType.includes('BOTTLE')) return 'bottle';
    if (unitType.includes('PIECE')) return 'piece';
    if (unitType.includes('PACK')) return 'pack';
    if (unitType.includes('KG')) return 'kg';
    return 'kg';
  };
  
  // Get selected product details for Add Stock preview
  const selectedProduct = allProducts.find(p => p.id === addStockForm.productId);
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">NAS Shop</h1>
          <p className="text-slate-500">Wholesale & Retail Sales</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadData} disabled={isLoading}>
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </Button>
          <Button onClick={() => setIsAddStockModalOpen(true)} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
            <PackagePlus size={18} className="mr-2" />
            Add Stock
          </Button>
          <Button onClick={() => { setSaleForm({...saleForm, saleType: activeTab}); setIsSaleModalOpen(true); }} className="bg-green-600 hover:bg-green-700">
            <Plus size={18} className="mr-2" />
            Record Sale
          </Button>
          <Button onClick={() => setIsConvertModalOpen(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
            <ArrowRightLeft size={18} className="mr-2" />
            Convert to Retail
          </Button>
          <Button onClick={() => setIsTransferToBodegaOpen(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="transfer-to-bodega-btn">
            <Undo2 size={18} className="mr-2" />
            Return to Bodega
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Today's Sales</p>
                <p className="text-xl font-bold text-green-600">₱{todayTotal.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShoppingBag className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Wholesale Sales</p>
                <p className="text-xl font-bold">{wholesaleSales.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Store className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Retail Sales</p>
                <p className="text-xl font-bold">{retailSales.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Package className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Products</p>
                <p className="text-xl font-bold">{products.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          type="text"
          placeholder="Search products by name, SKU, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 py-5 text-base bg-white border-slate-200"
        />
        {searchQuery && (
          <button 
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        )}
      </div>
      
      {/* Search Results Count */}
      {searchQuery && (
        <p className="text-sm text-slate-500">
          Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </p>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="wholesale" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            Wholesale (Per Sack)
          </TabsTrigger>
          <TabsTrigger value="retail" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            Retail (Per Kg/Pack)
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            Sales History
          </TabsTrigger>
          <TabsTrigger value="movements" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            Stock Movements
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="wholesale" className="mt-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Wholesale Stock</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">
                    {searchQuery ? `No products found matching "${searchQuery}"` : 'No products available. Add products in Products page first.'}
                  </p>
                ) : (
                  filteredProducts.map(product => {
                    const wholesaleUnit = getWholesaleUnit(product);
                    return (
                    <div key={product.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-slate-500">₱{(product.sellingPricePerSack || product.wholesalePrice || 0).toLocaleString()} per {wholesaleUnit.singular}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${(product.shopStock?.wholesale || 0) > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                            {product.shopStock?.wholesale || 0}
                          </p>
                          <p className="text-xs text-slate-500">{wholesaleUnit.plural}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => openEditModal(product, 'wholesale')}
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit quantity"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => {
                              const defaultType = wholesaleUnit.singular === 'case' ? 'case' : wholesaleUnit.singular === 'box' ? 'box' : 'sack';
                              setAddStockForm({ productId: product.id, quantity: 0, stockType: defaultType });
                              setIsAddStockModalOpen(true);
                            }}
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Add stock"
                          >
                            <Plus size={16} />
                          </button>
                          {(product.shopStock?.wholesale || 0) > 0 && (
                            <button
                              onClick={() => openConvertModal(product)}
                              className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                              title="Convert to retail"
                            >
                              <ArrowRightLeft size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );})
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="retail" className="mt-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Retail Stock</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">
                    {searchQuery ? `No products found matching "${searchQuery}"` : 'No products available. Add products in Products page first.'}
                  </p>
                ) : (
                  filteredProducts.map(product => {
                    const retailUnit = getRetailUnit(product);
                    const wholesaleUnit = getWholesaleUnit(product);
                    return (
                    <div key={product.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium">{product.name}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>₱{(product.sellingPricePerKg || product.retailPrice || 0).toLocaleString()} per {retailUnit.singular}</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {product.conversionRate || 1} {retailUnit.plural}/{wholesaleUnit.singular}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${(product.shopStock?.retail || 0) > 0 ? 'text-purple-600' : 'text-slate-400'}`}>
                            {product.shopStock?.retail || 0}
                          </p>
                          <p className="text-xs text-slate-500">{retailUnit.plural}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => openEditModal(product, 'retail')}
                            className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title="Edit quantity"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => {
                              const defaultType = retailUnit.singular === 'bottle' ? 'bottle' : retailUnit.singular === 'piece' ? 'piece' : retailUnit.singular === 'pack' ? 'pack' : 'kilogram';
                              setAddStockForm({ productId: product.id, quantity: 0, stockType: defaultType });
                              setIsAddStockModalOpen(true);
                            }}
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Add stock"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )})
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="history" className="mt-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Recent Sales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sales.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No sales recorded yet</p>
                ) : (
                  sales.slice(0, 20).map(sale => (
                    <div key={sale.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{sale.productName}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span className={`px-2 py-0.5 rounded ${sale.type === 'wholesale' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {sale.type}
                          </span>
                          <span>{sale.quantity} units</span>
                          <span>• {sale.customerName || 'Walk-in'}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">₱{(sale.total || 0).toLocaleString()}</p>
                        <p className="text-xs text-slate-500">{new Date(sale.timestamp).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Stock Movements Tab */}
        <TabsContent value="movements" className="mt-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="text-indigo-600" size={20} />
                Stock Movement History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  </div>
                ) : stockHistory.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No stock movements recorded yet</p>
                ) : (
                  stockHistory.map((tx, idx) => {
                    // Determine badge color and text based on transaction type
                    let badgeClass = 'bg-slate-100 text-slate-700';
                    let badgeText = tx.type;
                    let quantityColor = 'text-slate-600';
                    let icon = '📦';
                    
                    if (tx.type === 'shop_stock_addition') {
                      badgeClass = 'bg-green-100 text-green-700';
                      badgeText = 'Stock Added';
                      quantityColor = 'text-green-600';
                      icon = '➕';
                    } else if (tx.type === 'convert_wholesale_to_retail') {
                      badgeClass = 'bg-purple-100 text-purple-700';
                      badgeText = 'Converted';
                      quantityColor = 'text-purple-600';
                      icon = '🔄';
                    } else if (tx.type === 'pos_sale') {
                      badgeClass = 'bg-blue-100 text-blue-700';
                      badgeText = 'POS Sale';
                      quantityColor = 'text-red-600';
                      icon = '🛒';
                    } else if (tx.type === 'receive_from_supplier') {
                      badgeClass = 'bg-emerald-100 text-emerald-700';
                      badgeText = 'Received';
                      quantityColor = 'text-emerald-600';
                      icon = '📥';
                    } else if (tx.type === 'transfer_to_bodega') {
                      badgeClass = 'bg-amber-100 text-amber-700';
                      badgeText = 'Returned to Bodega';
                      quantityColor = 'text-amber-600';
                      icon = '🏚️';
                    }
                    
                    return (
                      <div key={tx.id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{icon}</span>
                          <div>
                            <p className="font-medium">{tx.productName || 'Unknown Product'}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                              <span className={`px-2 py-0.5 rounded text-xs ${badgeClass}`}>
                                {badgeText}
                              </span>
                              <span className="capitalize">{tx.stockType || tx.unit || ''}</span>
                              {tx.reference && <span>• Ref: {tx.reference}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${quantityColor}`}>
                            {tx.quantity > 0 ? '+' : ''}{tx.quantity}
                          </p>
                          <p className="text-xs text-slate-500">
                            {tx.timestamp ? new Date(tx.timestamp).toLocaleString('en-PH', { 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            }) : ''}
                          </p>
                          {tx.performedBy && (
                            <p className="text-xs text-slate-400">by {tx.performedBy}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Record Sale Modal */}
      <Dialog open={isSaleModalOpen} onOpenChange={setIsSaleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Sale Type</Label>
              <Select value={saleForm.saleType} onValueChange={(v) => setSaleForm({...saleForm, saleType: v, productId: ''})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wholesale">Wholesale (Per Sack)</SelectItem>
                  <SelectItem value="retail">Retail (Per Kg/Pack)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Product</Label>
              <Select value={saleForm.productId} onValueChange={(v) => setSaleForm({...saleForm, productId: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => (p.shopStock?.[saleForm.saleType] || 0) > 0).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.shopStock?.[saleForm.saleType] || 0} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={saleForm.quantity || ''}
                  onChange={(e) => setSaleForm({...saleForm, quantity: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Discount (₱)</Label>
                <Input
                  type="number"
                  value={saleForm.discount || ''}
                  onChange={(e) => setSaleForm({...saleForm, discount: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Customer Name</Label>
              <Input
                value={saleForm.customerName}
                onChange={(e) => setSaleForm({...saleForm, customerName: e.target.value})}
                placeholder="Optional - Walk-in customer"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={saleForm.paymentMethod} onValueChange={(v) => setSaleForm({...saleForm, paymentMethod: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                  <SelectItem value="credit">Credit/Utang</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Price Preview */}
            {saleForm.productId && saleForm.quantity > 0 && (
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span>₱{((products.find(p => p.id === saleForm.productId)?.[saleForm.saleType === 'wholesale' ? 'wholesalePrice' : 'retailPrice'] || 0) * saleForm.quantity).toLocaleString()}</span>
                </div>
                {saleForm.discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Discount:</span>
                    <span>-₱{saleForm.discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t">
                  <span>Total:</span>
                  <span className="text-green-600">
                    ₱{(((products.find(p => p.id === saleForm.productId)?.[saleForm.saleType === 'wholesale' ? 'wholesalePrice' : 'retailPrice'] || 0) * saleForm.quantity) - (saleForm.discount || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSaleModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRecordSale} disabled={isSaving} className="bg-green-600">
              {isSaving ? 'Recording...' : 'Record Sale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Convert Stock Modal */}
      <Dialog open={isConvertModalOpen} onOpenChange={setIsConvertModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="text-purple-600" size={24} />
              Convert Wholesale to Retail
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Product Selection */}
            <div>
              <Label className="text-base font-semibold">Select Product *</Label>
              <Select 
                value={convertForm.productId} 
                onValueChange={(v) => {
                  const product = allProducts.find(p => p.id === v);
                  const wholesaleUnit = product ? getWholesaleUnit(product) : { singular: 'sack' };
                  const retailUnit = product ? getRetailUnit(product) : { singular: 'kilogram' };
                  setConvertForm({
                    ...convertForm, 
                    productId: v, 
                    sacksToConvert: 1,
                    sourceUnit: wholesaleUnit.singular,
                    targetUnit: retailUnit.singular,
                    conversionRate: product?.conversionRate || 50
                  });
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a product..." />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.filter(p => (p.shopStock?.wholesale || 0) > 0 && p.unitType !== 'NO_CONVERSION').map(product => {
                    const wholesaleUnit = getWholesaleUnit(product);
                    return (
                      <SelectItem key={product.id} value={product.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{product.name}</span>
                          <span className="text-xs text-blue-600 ml-2">
                            ({product.shopStock?.wholesale || 0} {wholesaleUnit.plural})
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {allProducts.filter(p => (p.shopStock?.wholesale || 0) > 0).length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No products with wholesale stock available to convert.
                </p>
              )}
            </div>
            
            {/* Source and Target Unit Selection - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Converting From</Label>
                <Select 
                  value={convertForm.sourceUnit} 
                  onValueChange={(v) => setConvertForm({...convertForm, sourceUnit: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sack">Sack</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="case">Case</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Converting To</Label>
                <Select 
                  value={convertForm.targetUnit} 
                  onValueChange={(v) => setConvertForm({...convertForm, targetUnit: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kilogram">Kilogram (Kg)</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                    <SelectItem value="piece">Piece</SelectItem>
                    <SelectItem value="bottle">Bottle</SelectItem>
                    <SelectItem value="liter">Liter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Conversion Rate Input */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <Label className="text-base font-semibold text-blue-800">
                Conversion Rate *
              </Label>
              <p className="text-xs text-blue-600 mb-2">
                How many {convertForm.targetUnit}s are in 1 {convertForm.sourceUnit}?
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">1 {convertForm.sourceUnit} =</span>
                <div className="flex items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setConvertForm({
                      ...convertForm, 
                      conversionRate: Math.max(1, (convertForm.conversionRate || 1) - 1)
                    })}
                  >
                    <ChevronDown size={18} />
                  </Button>
                  <Input
                    type="number"
                    min="1"
                    value={convertForm.conversionRate || ''}
                    onChange={(e) => setConvertForm({
                      ...convertForm, 
                      conversionRate: parseFloat(e.target.value) || 1
                    })}
                    className="w-20 mx-2 text-center text-lg font-bold"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => setConvertForm({
                      ...convertForm, 
                      conversionRate: (convertForm.conversionRate || 1) + 1
                    })}
                  >
                    <ChevronUp size={18} />
                  </Button>
                </div>
                <span className="text-sm font-medium">{convertForm.targetUnit}s</span>
              </div>
            </div>
            
            {/* Show selected product info */}
            {convertForm.productId && (() => {
              const product = allProducts.find(p => p.id === convertForm.productId);
              if (!product) return null;
              const sourceLabel = getStockUnitLabel(convertForm.sourceUnit);
              const targetLabel = getStockUnitLabel(convertForm.targetUnit);
              return (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium">{product.name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-blue-600">
                      <strong>{product.shopStock?.wholesale || 0}</strong> {sourceLabel} available
                    </span>
                    <span className="text-purple-600">
                      <strong>{product.shopStock?.retail || 0}</strong> {targetLabel} in retail
                    </span>
                  </div>
                </div>
              );
            })()}
            
            {/* Quantity to Convert with Up/Down Arrows */}
            <div>
              <Label className="text-base font-semibold">
                Quantity to Convert *
              </Label>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => setConvertForm({
                    ...convertForm, 
                    sacksToConvert: Math.max(1, (convertForm.sacksToConvert || 1) - 1)
                  })}
                  disabled={convertForm.sacksToConvert <= 1}
                >
                  <ChevronDown size={24} />
                </Button>
                <Input
                  type="number"
                  min="1"
                  max={allProducts.find(p => p.id === convertForm.productId)?.shopStock?.wholesale || 0}
                  value={convertForm.sacksToConvert || ''}
                  onChange={(e) => setConvertForm({
                    ...convertForm, 
                    sacksToConvert: parseFloat(e.target.value) || 1
                  })}
                  className="w-24 text-center text-xl font-bold"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-12 w-12"
                  onClick={() => {
                    const max = allProducts.find(p => p.id === convertForm.productId)?.shopStock?.wholesale || 0;
                    setConvertForm({
                      ...convertForm, 
                      sacksToConvert: Math.min(max, (convertForm.sacksToConvert || 0) + 1)
                    });
                  }}
                  disabled={convertForm.sacksToConvert >= (allProducts.find(p => p.id === convertForm.productId)?.shopStock?.wholesale || 0)}
                >
                  <ChevronUp size={24} />
                </Button>
                <span className="text-lg font-medium text-slate-600">
                  {getStockUnitLabel(convertForm.sourceUnit)}
                </span>
              </div>
            </div>
            
            {/* Conversion Preview */}
            {convertForm.productId && convertForm.sacksToConvert > 0 && (() => {
              const product = allProducts.find(p => p.id === convertForm.productId);
              if (!product) return null;
              const conversionRate = convertForm.conversionRate || 1;
              const resultQty = convertForm.sacksToConvert * conversionRate;
              const wholesaleStock = product.shopStock?.wholesale || 0;
              const retailStock = product.shopStock?.retail || 0;
              const sourceLabel = getStockUnitLabel(convertForm.sourceUnit);
              const targetLabel = getStockUnitLabel(convertForm.targetUnit);
              const isValid = convertForm.sacksToConvert <= wholesaleStock;
              
              return (
                <div className={`p-4 rounded-lg border-2 ${isValid ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-center gap-6">
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${isValid ? 'text-blue-600' : 'text-red-600'}`}>
                        {convertForm.sacksToConvert}
                      </p>
                      <p className="text-sm text-slate-500">{sourceLabel}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowRightLeft className={isValid ? 'text-purple-500' : 'text-red-500'} size={28} />
                      <span className="text-xs text-slate-400 mt-1">converts to</span>
                    </div>
                    <div className="text-center">
                      <p className={`text-3xl font-bold ${isValid ? 'text-purple-600' : 'text-red-600'}`}>
                        {resultQty}
                      </p>
                      <p className="text-sm text-slate-500">{targetLabel}</p>
                    </div>
                  </div>
                  
                  {isValid ? (
                    <div className="mt-4 pt-3 border-t border-purple-200 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Wholesale after:</span>
                        <span className="font-medium text-blue-700 ml-1">{wholesaleStock - convertForm.sacksToConvert} {sourceLabel}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Retail after:</span>
                        <span className="font-medium text-purple-700 ml-1">{retailStock + resultQty} {targetLabel}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-3 text-center text-sm text-red-600 font-medium">
                      Insufficient stock! Only {wholesaleStock} {sourceLabel} available.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsConvertModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                // Update product conversion rate before converting
                const product = allProducts.find(p => p.id === convertForm.productId);
                if (product && convertForm.conversionRate !== product.conversionRate) {
                  // Save the new conversion rate to the product
                  axios.put(`${API}/api/nas/products/${convertForm.productId}`, {
                    conversionRate: convertForm.conversionRate
                  }).catch(err => console.error('Failed to update conversion rate:', err));
                }
                handleConvertStock();
              }}
              disabled={isSaving || !convertForm.productId || convertForm.sacksToConvert <= 0 || 
                convertForm.sacksToConvert > (allProducts.find(p => p.id === convertForm.productId)?.shopStock?.wholesale || 0)} 
              className="bg-purple-600 hover:bg-purple-700 px-6"
            >
              {isSaving ? 'Converting...' : 'Convert Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Stock Modal */}
      <Dialog open={isAddStockModalOpen} onOpenChange={setIsAddStockModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="text-green-600" size={24} />
              Add Stock to Shop
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Product Selection */}
            <div>
              <Label className="text-base font-semibold">Select Product *</Label>
              <Select 
                value={addStockForm.productId} 
                onValueChange={(v) => setAddStockForm({...addStockForm, productId: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a product..." />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{product.name}</span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({product.shopStock?.wholesale || 0} sacks)
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {allProducts.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No products found. Please add products first in NAS → Products page.
                </p>
              )}
            </div>
            
            {/* Stock Type */}
            <div>
              <Label>Stock Type</Label>
              <Select 
                value={addStockForm.stockType} 
                onValueChange={(v) => setAddStockForm({...addStockForm, stockType: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wholesale" className="font-medium text-blue-700">── Wholesale Units ──</SelectItem>
                  <SelectItem value="sack">Sack</SelectItem>
                  <SelectItem value="box">Box</SelectItem>
                  <SelectItem value="case">Case</SelectItem>
                  <SelectItem value="retail" className="font-medium text-purple-700 mt-2">── Retail Units ──</SelectItem>
                  <SelectItem value="kilogram">Kilogram (Kg)</SelectItem>
                  <SelectItem value="pack">Pack</SelectItem>
                  <SelectItem value="piece">Piece</SelectItem>
                  <SelectItem value="bottle">Bottle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Quantity */}
            <div>
              <Label className="text-base font-semibold">
                Quantity ({getStockUnitLabel(addStockForm.stockType)}) *
              </Label>
              <Input
                type="number"
                placeholder="Enter quantity"
                value={addStockForm.quantity || ''}
                onChange={(e) => setAddStockForm({...addStockForm, quantity: parseFloat(e.target.value) || 0})}
                className="mt-1 text-lg"
              />
            </div>
            
            {/* Preview */}
            {selectedProduct && addStockForm.quantity > 0 && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-2">{selectedProduct.name}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-600">Current Stock:</span>
                    <span className="font-medium ml-1">
                      {isWholesaleType(addStockForm.stockType)
                        ? `${selectedProduct.shopStock?.wholesale || 0} wholesale`
                        : `${selectedProduct.shopStock?.retail || 0} retail`
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">Adding:</span>
                    <span className="font-medium text-green-600 ml-1">
                      +{addStockForm.quantity} {getStockUnitLabel(addStockForm.stockType)}
                    </span>
                  </div>
                  <div className="col-span-2 pt-2 border-t border-green-200">
                    <span className="text-slate-600">New Total:</span>
                    <span className="font-bold text-green-700 ml-1">
                      {isWholesaleType(addStockForm.stockType)
                        ? `${(selectedProduct.shopStock?.wholesale || 0) + addStockForm.quantity} wholesale`
                        : `${(selectedProduct.shopStock?.retail || 0) + addStockForm.quantity} retail`
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddStockModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddStock} 
              disabled={isSaving || !addStockForm.productId || addStockForm.quantity <= 0} 
              className="bg-green-600 hover:bg-green-700"
            >
              {isSaving ? 'Adding...' : 'Add Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Stock Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" size={24} />
              Edit Stock Quantity
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingProduct && (
              <>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium text-lg">{editingProduct.name}</p>
                  <p className="text-sm text-slate-500">
                    {activeTab === 'wholesale' ? 'Wholesale (Sacks)' : 'Retail (Kg/Packs)'}
                  </p>
                </div>
                
                <div>
                  <Label className="text-base font-semibold">
                    New Quantity ({activeTab === 'wholesale' ? 'Sacks' : 'Kg/Packs'})
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={editQuantity}
                    onChange={(e) => setEditQuantity(parseFloat(e.target.value) || 0)}
                    className="mt-2 text-lg text-center"
                    autoFocus
                  />
                </div>
                
                <div className="flex items-center justify-between text-sm bg-blue-50 p-3 rounded-lg">
                  <span className="text-slate-600">Current:</span>
                  <span className="font-medium">
                    {activeTab === 'wholesale' 
                      ? editingProduct.shopStock?.wholesale || 0
                      : editingProduct.shopStock?.retail || 0
                    } {activeTab === 'wholesale' ? 'sacks' : 'kg/packs'}
                  </span>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => handleEditStock(activeTab)} 
              disabled={isSaving} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Transfer to Bodega Modal */}
      <Dialog open={isTransferToBodegaOpen} onOpenChange={setIsTransferToBodegaOpen}>
        <DialogContent className="max-w-lg" data-testid="transfer-to-bodega-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="text-purple-600" size={24} />
              <ArrowRight size={18} className="text-slate-400" />
              <Warehouse className="text-amber-600" size={24} />
              Return Stock to Bodega
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
              <strong>Note:</strong> This will deduct stock from NAS Shop and return it to the Bodega warehouse.
            </div>
            
            {/* Stock Type */}
            <div>
              <Label className="text-base font-semibold">Stock Type</Label>
              <Select
                value={transferForm.stockType}
                onValueChange={(v) => setTransferForm({...transferForm, stockType: v, productId: '', quantity: 0})}
              >
                <SelectTrigger className="mt-1" data-testid="transfer-stock-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wholesale">Wholesale (Sacks/Boxes/Cases)</SelectItem>
                  <SelectItem value="retail">Retail (Kg/Packs/Pieces)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Product Selection */}
            <div>
              <Label className="text-base font-semibold">Select Product *</Label>
              <Select
                value={transferForm.productId}
                onValueChange={(v) => setTransferForm({...transferForm, productId: v, quantity: 0})}
              >
                <SelectTrigger className="mt-1" data-testid="transfer-product-select">
                  <SelectValue placeholder="Choose a product..." />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.filter(p => (p.shopStock?.[transferForm.stockType] || 0) > 0).map(product => {
                    const stock = product.shopStock?.[transferForm.stockType] || 0;
                    return (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} ({stock} {transferForm.stockType === 'wholesale' ? 'sacks' : 'units'} in shop)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {allProducts.filter(p => (p.shopStock?.[transferForm.stockType] || 0) > 0).length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No products with {transferForm.stockType} stock in shop.
                </p>
              )}
            </div>
            
            {/* Product Info */}
            {transferForm.productId && (() => {
              const product = allProducts.find(p => p.id === transferForm.productId);
              if (!product) return null;
              const shopQty = product.shopStock?.[transferForm.stockType] || 0;
              const bodegaQty = product.bodegaStock || 0;
              return (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium">{product.name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="text-purple-600">
                      <strong>{shopQty}</strong> in shop ({transferForm.stockType})
                    </span>
                    <span className="text-amber-600">
                      <strong>{bodegaQty}</strong> in bodega
                    </span>
                  </div>
                </div>
              );
            })()}
            
            {/* Quantity */}
            <div>
              <Label className="text-base font-semibold">Quantity to Return *</Label>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  type="button" variant="outline" size="icon" className="h-12 w-12"
                  onClick={() => setTransferForm({...transferForm, quantity: Math.max(0, transferForm.quantity - 1)})}
                  disabled={transferForm.quantity <= 0}
                >
                  <ChevronDown size={24} />
                </Button>
                <Input
                  type="number" min="0"
                  max={allProducts.find(p => p.id === transferForm.productId)?.shopStock?.[transferForm.stockType] || 0}
                  value={transferForm.quantity || ''}
                  onChange={(e) => setTransferForm({...transferForm, quantity: parseFloat(e.target.value) || 0})}
                  className="w-24 text-center text-xl font-bold"
                  data-testid="transfer-quantity-input"
                />
                <Button
                  type="button" variant="outline" size="icon" className="h-12 w-12"
                  onClick={() => {
                    const max = allProducts.find(p => p.id === transferForm.productId)?.shopStock?.[transferForm.stockType] || 0;
                    setTransferForm({...transferForm, quantity: Math.min(max, transferForm.quantity + 1)});
                  }}
                  disabled={transferForm.quantity >= (allProducts.find(p => p.id === transferForm.productId)?.shopStock?.[transferForm.stockType] || 0)}
                >
                  <ChevronUp size={24} />
                </Button>
                <span className="text-lg font-medium text-slate-600">
                  {transferForm.stockType === 'wholesale' ? 'sacks' : 'units'}
                </span>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Reason / Notes</Label>
              <Input
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                placeholder="e.g., Excess stock, wrong allocation..."
                className="mt-1"
                data-testid="transfer-notes-input"
              />
            </div>
            
            {/* Preview */}
            {transferForm.productId && transferForm.quantity > 0 && (() => {
              const product = allProducts.find(p => p.id === transferForm.productId);
              if (!product) return null;
              const shopQty = product.shopStock?.[transferForm.stockType] || 0;
              const bodegaQty = product.bodegaStock || 0;
              const isValid = transferForm.quantity <= shopQty;
              
              return (
                <div className={`p-4 rounded-lg border-2 ${isValid ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-center gap-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Shop After</p>
                      <p className={`text-2xl font-bold ${isValid ? 'text-purple-600' : 'text-red-600'}`}>
                        {isValid ? shopQty - transferForm.quantity : '---'}
                      </p>
                    </div>
                    <ArrowRight className={isValid ? 'text-amber-500' : 'text-red-500'} size={28} />
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Bodega After</p>
                      <p className={`text-2xl font-bold ${isValid ? 'text-amber-600' : 'text-red-600'}`}>
                        {isValid ? bodegaQty + transferForm.quantity : '---'}
                      </p>
                    </div>
                  </div>
                  {!isValid && (
                    <p className="mt-3 text-center text-sm text-red-600 font-medium">
                      Insufficient stock! Only {shopQty} available in shop.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsTransferToBodegaOpen(false)}>Cancel</Button>
            <Button
              onClick={handleTransferToBodega}
              disabled={isSaving || !transferForm.productId || transferForm.quantity <= 0 ||
                transferForm.quantity > (allProducts.find(p => p.id === transferForm.productId)?.shopStock?.[transferForm.stockType] || 0)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="transfer-submit-btn"
            >
              {isSaving ? 'Transferring...' : 'Return to Bodega'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASShop;
